<template>
  <v-dialog
    v-model="internalValue"
    class="mb-5"
    max-width="900"
    transition="dialog-bottom-transition"
    scrollable
    persistent
    @keydown.esc="closeAddNewUserDialog"
  >
    <v-card class="ds-dialog">
      <AddNewUserDialogHeader
        :dialog-icon="dialogIcon"
        :is-edit-mode="isEditMode"
        :user-type-label="userTypeLabel"
        :user="user"
        @close="closeAddNewUserDialog"
        @help="startTour"
      />
      <!-- Stepper sem cartão próprio (flat/transparente): as seções de cada
           etapa ficam direto no corpo areia, como nos demais diálogos. -->
      <v-card-text class="ds-dialog-body">
        <v-stepper v-model="currentStep" flat class="transparent">
          <v-stepper-header>
            <v-stepper-step
              :complete="currentStep > 1"
              step="1"
              data-tour="np-cpf"
            >
              Verificar CPF
            </v-stepper-step>
            <v-divider />
            <v-stepper-step step="2" data-tour="np-dados">
              Preencher dados
            </v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <AddNewUserDialogCpfStep
              ref="cpfStep"
              :cpf-input="cpfInput"
              :cpf-checked="cpfChecked"
              :cpf-exists="cpfExists"
              :existing-patient-name="existingPatientName"
              :existing-already-linked="existingAlreadyLinked"
              :existing-user-role-id="existingUserRoleId"
              :should-show-clinic-selection-on-cpf-step="
                shouldShowClinicSelectionOnCpfStep
              "
              :clinics="clinics"
              :selected-clinics="selectedClinics"
              :loading-cpf="loadingCpf"
              :erro="erro"
              :only-digits="onlyDigits"
              @update:cpf-input="cpfInput = $event"
              @update:selected-clinics="selectedClinics = $event"
              @check-cpf="checkCpf"
              @link-existing-patient="linkExistingPatient"
            />
            <AddNewUserDialogFormStep
              ref="formStep"
              :user="user"
              :email-rules="emailRules"
              :gender-options="genderOptions"
              :phone-types="phoneTypes"
              :target-user-type="targetUserType"
              :is-edit-mode="isEditMode"
              :is-under18="isUnder18"
              :patient-age="patientAge"
              :is-minor-enabled="isMinorEnabled"
              :guardian-cpf-input="guardianCpfInput"
              :guardian-cpf-rules="guardianCpfRules"
              :guardian-loading="guardianLoading"
              :guardian-checked="guardianChecked"
              :guardian-exists="guardianExists"
              :guardian-name="guardianName"
              :guardian-error="guardianError"
              :guardian-not-found="guardianNotFound"
              :loading-c-e-p="loadingCEP"
              :should-show-clinic-selection="shouldShowClinicSelection"
              :clinics="clinics"
              :selected-clinics="selectedClinics"
              :selected-referral-type="selectedReferralType"
              :friend-referral-name="friendReferralName"
              :doctor-referral-name="doctorReferralName"
              :custom-referral-source="customReferralSource"
              :erro="erro"
              :loading="loading"
              :is-external-guardian="!!externalGuardianId"
              @update:user="user = $event"
              @update:is-minor-enabled="isMinorEnabled = $event"
              @update:guardian-cpf-input="guardianCpfInput = $event"
              @update:selected-clinics="selectedClinics = $event"
              @update:friend-referral-name="onFriendReferralNameUpdate($event)"
              @update:doctor-referral-name="onDoctorReferralNameUpdate($event)"
              @update:custom-referral-source="
                onCustomReferralSourceUpdate($event)
              "
              @search-guardian="searchGuardianByCpf"
              @register-guardian="openGuardianRegister"
              @reset-guardian="resetGuardian"
              @buscar-cep="buscarEnderecoPorCep"
              @add-phone="addPhones"
              @remove-phone="removePhones"
              @define-main-phone="defineMainPhone"
              @handle-referral-change="handleReferralChange"
              @go-back="goBackToStep1"
              @close="closeAddNewUserDialog"
              @save-user="saveUser"
            />
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
    </v-card>

    <!-- Cadastro do responsável "na hora" (quando o CPF não existe).
         Reaproveita o próprio diálogo, já no passo de dados, com o CPF
         preenchido. Ao concluir, o responsável recém-criado é buscado e
         vinculado ao menor automaticamente. -->
    <AddNewUserDialog
      v-if="guardianRegisterOpen"
      v-model="guardianRegisterOpen"
      target-type="patient"
      :prefill-cpf="onlyDigits(guardianCpfInput)"
      @updatePersonsPage="onGuardianCreated"
    />
  </v-dialog>
</template>
<script>
import http from "@/services/http";
import { getMyClinics } from "@/services/clinicService";
import { useSnackbar } from "@/composables/useSnackbar";
import tourDialogMixin from "@/features/tour/tourDialogMixin";
import AddNewUserDialogHeader from "./components/AddNewUserDialogHeader.vue";
import AddNewUserDialogCpfStep from "./components/AddNewUserDialogCpfStep.vue";
import AddNewUserDialogFormStep from "./components/AddNewUserDialogFormStep.vue";
import { calculateAge } from "@/utils/calculateAge.js";
import { hasUnreadableFields } from "@/utils/unreadableFields";
const REFERRAL_TYPES = {
  FRIEND: "Indicação de um amigo",
  DOCTOR: "Médico",
  OTHER: "Outro",
};
const PREDEFINED_REFERRALS = ["Google", "Instagram"];
function createEmptyUser() {
  return {
    name: "",
    email: "",
    cpf: "",
    gender: "",
    birth_date: "",
    status: 1,
    crm: "",
    clinic_id: null,
    referral_source: "",
    phones: [{ number: "", type: "celular", is_primary: true }],
    address: {
      cep: "",
      street: "",
      number: "",
      complement: "", // ✅ Task 1
      neighborhood: "",
      city: "",
      state: "",
    },
    formStepKey: 0,
  };
}
export default {
  name: "AddNewUserDialog",
  mixins: [tourDialogMixin],
  components: {
    AddNewUserDialogHeader,
    AddNewUserDialogCpfStep,
    AddNewUserDialogFormStep,
  },
  props: {
    value: { type: Boolean, default: false },
    userId: { type: [Number, String], default: null },
    targetType: { type: String, default: "patient" },
    externalGuardianId: { type: Number, default: null },
    externalGuardianAddress: { type: Object, default: null },
    // Quando informado (só dígitos), abre o diálogo já no passo de dados com o
    // CPF preenchido — usado no cadastro "na hora" do responsável.
    prefillCpf: { type: String, default: "" },
  },
  setup() {
    const { success, error, warning, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, erroDeRequisicao };
  },
  data() {
    return {
      isEditMode: false,
      // Cadastro carregado com campo que não abriu no cofre: não pode salvar.
      hasUnreadableData: false,
      editingUserId: null,
      userType: "",
      targetUserType: "patient",
      currentStep: 1,
      cpfInput: "",
      loadingCpf: false,
      cpfExists: false,
      cpfChecked: false,
      existingPatientName: "",
      existingAlreadyLinked: false,
      existingUserRoleId: null, // ✅ Task 3: papel do usuário existente (1=médico, 2=paciente, 3=assistente)
      loading: false,
      erro: "",
      user: createEmptyUser(),
      clinics: [],
      selectedClinics: [],
      phoneTypes: [
        { text: "Celular", value: "celular" },
        { text: "Fixo", value: "fixo" },
      ],
      genderOptions: [
        { text: "Masculino", value: "Masculino" },
        { text: "Feminino", value: "Feminino" },
        { text: "Outro", value: "Outros" },
      ],
      loadingCEP: false,
      selectedReferralType: "",
      friendReferralName: "",
      doctorReferralName: "",
      customReferralSource: "",
      isMinorEnabled: false,
      guardianCpfInput: "",
      guardianLoading: false,
      guardianChecked: false,
      guardianExists: false,
      guardianName: "",
      guardianId: null,
      guardianError: "",
      guardianNotFound: false,
      guardianRegisterOpen: false,
      hasInitializedDialog: false,
    };
  },
  computed: {
    tourId() {
      return this.isEditMode ? "dialog-editar-pessoa" : "dialog-novo-paciente";
    },
    userTypeLabel() {
      return this.targetUserType === "doctor" ? "Médico" : "Paciente";
    },
    dialogIcon() {
      return this.targetUserType === "doctor"
        ? "mdi-doctor"
        : "mdi-account-plus";
    },
    shouldShowClinicSelection() {
      return (
        this.userType === "doctor_clinic" ||
        this.userType === "assistant" ||
        this.targetUserType === "doctor"
      );
    },
    shouldShowClinicSelectionOnCpfStep() {
      return (
        this.currentStep === 1 &&
        this.cpfChecked &&
        this.cpfExists &&
        (this.userType === "doctor_clinic" || this.userType === "assistant")
      );
    },
    apiConfig() {
      if (this.targetUserType === "doctor") {
        return {
          createUrl: `/api/doctors/create`,
          editUrl: `/api/doctors/edit/${this.editingUserId}`,
          loadUrl: `/api/user/get-user/${this.editingUserId}`,
        };
      }
      return {
        createUrl: `/api/register/create-patient-user`,
        editUrl: `/api/patient/edit-patient/${this.editingUserId}`,
        loadUrl: `/api/user/get-user/${this.editingUserId}`,
      };
    },
    patientAge() {
      return this.calculateAge(this.user.birth_date);
    },
    isUnder18() {
      const age = this.patientAge;
      return Number.isFinite(age) && age >= 0 && age < 18;
    },
    emailRules() {
      const isOptional =
        this.targetUserType === "patient" &&
        this.isUnder18 &&
        this.isMinorEnabled;
      if (isOptional) {
        return [(v) => !v || /.+@.+\..+/.test(v) || "Email inválido"];
      }
      return [
        (v) => !!v || "Email obrigatório",
        (v) => /.+@.+\..+/.test(v) || "Email inválido",
      ];
    },
    guardianCpfRules() {
      if (!(this.isUnder18 && this.isMinorEnabled)) return [];
      return [
        (v) => !!v || "CPF do responsável é obrigatório",
        (v) => this.onlyDigits(v).length === 11 || "CPF deve ter 11 dígitos",
      ];
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.openFromProps();
          // Tour guiado (create e edição têm tourId/roteiro próprios).
          this.tourOnOpen();
        } else {
          this.resetForm();
          this.hasInitializedDialog = false;
        }
      },
    },
    userId() {
      if (this.value) this.openFromProps();
    },
    targetType() {
      if (this.value) this.openFromProps();
    },
    "user.birth_date"() {
      if (this.externalGuardianId) return;

      if (!this.isUnder18) {
        this.isMinorEnabled = false;
        this.resetGuardian();
      }
      this.$nextTick(() => {
        this.$refs.formStep?.resetValidation?.();
      });
    },
    isMinorEnabled(val) {
      if (!val) this.resetGuardian();

      this.$nextTick(() => {
        this.$refs.formStep?.resetValidation?.();
      });
    },
    currentStep(val) {
      if (val === 2 && this.externalGuardianId) {
        this.applyExternalGuardian();
      }
    },
  },
  async mounted() {
    await this.init();
  },
  methods: {
    // fallback NaN preservado (isUnder18 usa Number.isFinite)
    calculateAge: (v) => calculateAge(v, { fallback: NaN }),
    // Tour guiado (tourDialogMixin fornece tourOnOpen/startTour).
    tourSteps() {
      // Modo edição: o CPF já foi resolvido, então o tour foca só nos dados.
      if (this.isEditMode) {
        return [
          {
            title: "Editar cadastro",
            description:
              "Você está editando uma pessoa já cadastrada. O CPF não muda — ajuste apenas os dados necessários e salve.",
          },
          {
            anchor: "np-dados",
            title: "Ajuste os dados",
            description:
              "Atualize nome, contato (telefones e o principal), e-mail e endereço (com busca por CEP). As alterações passam a valer em toda a plataforma — inclusive nos convites de atendimento.",
          },
        ];
      }

      return [
        {
          // Boas-vindas (sem âncora → card centralizado).
          title: "Cadastro em 2 passos",
          description:
            "Este assistente cria (ou vincula) uma pessoa em duas etapas simples. Vou explicar cada uma — e você pode reabrir este tour pelo botão “?” no topo.",
        },
        {
          anchor: "np-cpf",
          title: "1) Verifique o CPF",
          description:
            "Tudo começa pelo CPF, que evita cadastros duplicados. Ao verificar: se a pessoa JÁ existe no sistema, você só a vincula à(s) sua(s) clínica(s) — sem redigitar nada; se NÃO existe, o botão leva você ao passo 2 para o cadastro completo.",
        },
        {
          anchor: "np-dados",
          title: "2) Preencha os dados",
          description:
            "Aqui vão os dados da pessoa: nome, contato (um ou mais telefones, com um principal), e-mail e endereço (com busca automática pelo CEP). Se for paciente menor de idade, o sistema pede o responsável — que você pode buscar pelo CPF ou cadastrar na hora. Também é possível registrar como a pessoa chegou até você (indicação).",
        },
      ];
    },

    async init() {
      try {
        let userType = this.$store.getters["auth/userType"];
        if (!userType) {
          await this.$store.dispatch("auth/ensureAuth");
          userType = this.$store.getters["auth/userType"];
        }
        this.userType = userType || "";
        this.clinics = await getMyClinics();
        this.autoSelectSingleClinic();
      } catch (err) {
        console.error("Erro ao inicializar diálogo:", err);
      }
    },
    onlyDigits(value) {
      return (value || "").replace(/\D/g, "");
    },
    entityLabel() {
      return this.targetUserType === "doctor" ? "Médico" : "Paciente";
    },
    async openFromProps() {
      if (this.hasInitializedDialog) return;
      this.targetUserType = this.targetType;
      this.isMinorEnabled = false;
      this.resetGuardian();
      this.currentStep = 1;
      if (!this.clinics?.length) {
        this.clinics = await getMyClinics();
      }
      this.autoSelectSingleClinic();
      if (this.userId) {
        this.isEditMode = true;
        this.editingUserId = this.userId;
        await this.loadUser();
        this.currentStep = 2;
      } else {
        this.isEditMode = false;
        this.editingUserId = null;
        // Cadastro "na hora" do responsável: já sabemos que o CPF não existe,
        // então pula a verificação e vai direto para o formulário de dados.
        if (this.prefillCpf) {
          const cpf = this.onlyDigits(this.prefillCpf);
          this.cpfInput = cpf;
          this.user.cpf = cpf;
          this.cpfChecked = true;
          this.cpfExists = false;
          this.currentStep = 2;
        }
      }
      this.hasInitializedDialog = true;
    },
    async applyExternalGuardian() {
      this.isMinorEnabled = true;
      this.guardianId = this.externalGuardianId;
      this.guardianChecked = true;
      this.guardianExists = true;
      try {
        const res = await http.get(
          `/api/user/get-user/${this.externalGuardianId}`,
        );
        const guardianData = res.data?.data;
        this.guardianName = guardianData?.name || "";

        // Pré-preencher endereço do responsável
        if (this.externalGuardianAddress) {
          const addr = this.externalGuardianAddress;
          this.user.address.cep = addr.cep || "";
          this.user.address.street = addr.street || "";
          this.user.address.number = addr.number || "";
          this.user.address.complement = addr.complement || ""; // ✅ Task 1
          this.user.address.neighborhood = addr.neighborhood || "";
          this.user.address.city = addr.city || "";
          this.user.address.state = addr.state || "";
        }

        // Pré-selecionar clínicas do responsável (todas que o médico logado tem acesso)
        if (
          Array.isArray(guardianData?.clinics) &&
          guardianData.clinics.length > 0
        ) {
          const guardianClinicIds = guardianData.clinics.map((c) => c.id);
          this.selectedClinics = guardianClinicIds.filter((id) =>
            this.clinics.some((x) => x.id === id),
          );
        }
      } catch (err) {
        console.error("Erro ao carregar responsável externo:", err);
      }
    },
    closeAddNewUserDialog() {
      this.$emit("input", false);
    },
    resetForm() {
      this.currentStep = 1;
      this.cpfInput = "";
      this.loadingCpf = false;
      this.cpfChecked = false;
      this.cpfExists = false;
      this.existingPatientName = "";
      this.existingAlreadyLinked = false;
      this.existingUserRoleId = null; // ✅ Task 3
      this.user = createEmptyUser();
      this.loading = false;
      this.erro = "";
      this.selectedClinics = [];
      this.selectedReferralType = "";
      this.friendReferralName = "";
      this.doctorReferralName = "";
      this.customReferralSource = "";
      this.isEditMode = false;
      this.editingUserId = null;
      this.isMinorEnabled = false;
      this.resetGuardian();
      this.$nextTick(() => {
        this.$refs.formStep?.resetValidation?.();
        this.$refs.cpfStep?.resetValidation?.();
      });
    },
    addPhones() {
      if (this.user.phones.length >= 3) return;
      this.user.phones.push({ number: "", type: "celular", is_primary: false });
    },
    removePhones(index) {
      if (this.user.phones.length <= 1) return;
      this.user.phones.splice(index, 1);
    },
    defineMainPhone(index) {
      this.user.phones.forEach((phone, i) => {
        phone.is_primary = i === index;
      });
    },
    handleReferralChange(value) {
      this.selectedReferralType = value;
      this.friendReferralName = "";
      this.doctorReferralName = "";
      this.customReferralSource = "";
      const requiresDetail = [
        REFERRAL_TYPES.OTHER,
        REFERRAL_TYPES.FRIEND,
        REFERRAL_TYPES.DOCTOR,
      ];
      if (requiresDetail.includes(value)) {
        this.user.referral_source = "";
        return;
      }
      this.user.referral_source = value;
    },
    onFriendReferralNameUpdate(value) {
      this.friendReferralName = value;
      this.updateReferralSource();
    },
    onDoctorReferralNameUpdate(value) {
      this.doctorReferralName = value;
      this.updateReferralSource();
    },
    onCustomReferralSourceUpdate(value) {
      this.customReferralSource = value;
      this.updateReferralSource();
    },
    updateReferralSource() {
      const trimmed = (str) => str?.trim() || "";
      if (
        this.selectedReferralType === REFERRAL_TYPES.OTHER &&
        trimmed(this.customReferralSource)
      ) {
        this.user.referral_source = trimmed(this.customReferralSource);
        return;
      }
      if (
        this.selectedReferralType === REFERRAL_TYPES.FRIEND &&
        trimmed(this.friendReferralName)
      ) {
        this.user.referral_source = `${REFERRAL_TYPES.FRIEND}: ${trimmed(
          this.friendReferralName,
        )}`;
        return;
      }
      if (
        this.selectedReferralType === REFERRAL_TYPES.DOCTOR &&
        trimmed(this.doctorReferralName)
      ) {
        this.user.referral_source = `${REFERRAL_TYPES.DOCTOR}: ${trimmed(
          this.doctorReferralName,
        )}`;
      }
    },
    setupReferralSource(referralSource) {
      if (!referralSource) {
        this.selectedReferralType = "";
        this.friendReferralName = "";
        this.doctorReferralName = "";
        this.customReferralSource = "";
        this.user.referral_source = "";
        return;
      }
      if (PREDEFINED_REFERRALS.includes(referralSource)) {
        this.selectedReferralType = referralSource;
        this.user.referral_source = referralSource;
        return;
      }
      const friendPrefix = `${REFERRAL_TYPES.FRIEND}: `;
      if (referralSource.startsWith(friendPrefix)) {
        this.selectedReferralType = REFERRAL_TYPES.FRIEND;
        this.friendReferralName = referralSource.slice(friendPrefix.length);
        this.user.referral_source = referralSource;
        return;
      }
      const doctorPrefix = `${REFERRAL_TYPES.DOCTOR}: `;
      if (referralSource.startsWith(doctorPrefix)) {
        this.selectedReferralType = REFERRAL_TYPES.DOCTOR;
        this.doctorReferralName = referralSource.slice(doctorPrefix.length);
        this.user.referral_source = referralSource;
        return;
      }
      this.selectedReferralType = REFERRAL_TYPES.OTHER;
      this.customReferralSource = referralSource;
      this.user.referral_source = referralSource;
    },
    async buscarEnderecoPorCep() {
      const cep = this.onlyDigits(this.user.address.cep);
      if (!cep || cep.length !== 8) return;
      this.loadingCEP = true;
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        if (!response.ok) {
          throw new Error("Falha na requisição ViaCEP");
        }
        const data = await response.json();
        if (data.erro) {
          this.error("CEP não encontrado.");
          return;
        }
        this.user.address.street = data.logradouro || "";
        this.user.address.neighborhood = data.bairro || "";
        this.user.address.city = data.localidade || "";
        this.user.address.state = data.uf || "";
      } catch {
        this.error("Erro ao buscar o CEP.");
      } finally {
        this.loadingCEP = false;
      }
    },
    async checkCpf() {
      if (!this.$refs.cpfStep?.validate()) return;
      this.loadingCpf = true;
      this.erro = "";
      this.cpfChecked = false;
      this.cpfExists = false;
      this.existingPatientName = "";
      this.existingAlreadyLinked = false;
      this.existingUserRoleId = null; // ✅ Task 3
      const cpf = this.onlyDigits(this.cpfInput);
      try {
        const res = await http.get(`/api/patients/check-cpf/${cpf}`);
        this.cpfChecked = true;
        this.cpfExists = !!res.data.exists;
        if (!this.cpfExists) {
          // Backend armazena CPF só com dígitos; a máscara do campo formata na exibição.
          this.user.cpf = cpf;
          // O watcher de currentStep dispara applyExternalGuardian() quando
          // há responsável externo — não precisa chamar de novo aqui.
          this.currentStep = 2;
          return;
        }
        this.existingPatientName = res.data.name || "";
        this.existingAlreadyLinked = !!res.data.already_linked;
        this.existingUserRoleId = res.data.role_id ?? null; // ✅ Task 3
        if (this.userType === "doctor_solo") {
          await this.linkExistingPatient();
        }
      } catch (err) {
        console.error("Erro ao verificar CPF:", err);
        this.erro = "Erro ao verificar CPF.";
      } finally {
        this.loadingCpf = false;
      }
    },
    async linkExistingPatient() {
      this.loadingCpf = true;
      this.erro = "";
      try {
        let myUserId = this.$store.getters["auth/userId"];
        if (!myUserId) {
          await this.$store.dispatch("auth/ensureAuth");
          myUserId = this.$store.getters["auth/userId"];
        }
        const cpf = this.onlyDigits(this.cpfInput);
        const payload = { cpf };
        if (this.userType !== "assistant") payload.doctor_id = myUserId;
        if (
          (this.userType === "doctor_clinic" ||
            this.userType === "assistant") &&
          this.selectedClinics.length > 0
        ) {
          payload.clinic_ids = this.selectedClinics;
        }
        const res = await http.post(`/api/register/create-patient-user`, payload);
        this.success("Paciente vinculado com sucesso!");
        // O id deixa a lista de origem destacar quem acabou de entrar.
        this.$emit("updatePersonsPage", { personId: res?.data?.data?.id || null });
        this.closeAddNewUserDialog();
      } catch (err) {
        console.error("Erro ao vincular paciente:", err);
        const status = err.response?.status;
        if (status === 422) {
          const errors = err.response.data?.errors;
          const msg = errors ? Object.values(errors)[0]?.[0] : null;
          this.erroDeRequisicao(err, msg || "Erro de validação ao vincular paciente.");
          return;
        }
        if (status === 409) {
          this.erroDeRequisicao(err, 
            err.response?.data?.message ||
              "Não foi possível vincular esse CPF.",
          );
          return;
        }
        this.erroDeRequisicao(err, "Erro ao vincular paciente.");
      } finally {
        this.loadingCpf = false;
      }
    },
    async loadUser() {
      this.hasUnreadableData = false;
      try {
        if (!this.clinics?.length) {
          this.clinics = await getMyClinics();
        }
        const response = await http.get(this.apiConfig.loadUrl);
        const userData = response.data?.data;
        if (!userData) {
          throw new Error("Dados não encontrados na resposta da API");
        }
        // Campo que não abriu no cofre chega vazio (`unreadable_fields`). Salvar
        // o formulário assim gravaria o vazio por cima do dado verdadeiro — então
        // a edição fica travada até o cofre voltar (15/10/2026).
        this.hasUnreadableData = hasUnreadableFields(userData);
        if (this.hasUnreadableData) {
          this.warning(
            "Alguns dados deste cadastro não puderam ser abertos agora. A edição fica bloqueada para não apagar os dados reais — tente de novo em instantes.",
            8000,
          );
        }
        this.user = {
          ...createEmptyUser(),
          name: userData.name || "",
          email: userData.email || "",
          cpf: userData.cpf || "",
          gender: userData.gender || "",
          birth_date: userData.birth_date || "",
          crm: userData.crm || "",
          status: userData.status ?? 1,
          clinic_id: userData.clinic_id || null,
          referral_source: userData.referral_source || "",
          address: {
            cep: userData.address?.cep || "",
            street: userData.address?.street || "",
            number: userData.address?.number || "",
            complement: userData.address?.complement || "", // ✅ Task 1
            neighborhood: userData.address?.neighborhood || "",
            city: userData.address?.city || "",
            state: userData.address?.state || "",
          },
          phones:
            userData.phones?.length > 0
              ? userData.phones.map((p) => ({
                  id: p.id,
                  number: p.number || "",
                  type: p.type || "celular",
                  is_primary: !!p.is_primary,
                }))
              : [{ number: "", type: "celular", is_primary: true }],
        };
        this.selectedClinics = [];
        if (Array.isArray(userData.clinics) && userData.clinics.length > 0) {
          this.selectedClinics = userData.clinics
            .map((c) => c.id)
            .filter((id) => this.clinics.some((x) => x.id === id));
        } else if (
          userData.clinic_id &&
          this.clinics.some((x) => x.id === userData.clinic_id)
        ) {
          this.selectedClinics = [userData.clinic_id];
        }
        if (this.targetUserType === "patient" && userData.referral_source) {
          this.setupReferralSource(userData.referral_source);
        }
        this.isMinorEnabled = false;
        this.resetGuardian();
      } catch (err) {
        console.error("Erro ao carregar usuário:", err);
        this.erroDeRequisicao(err, 
          `Erro ao carregar dados do ${this.entityLabel().toLowerCase()}.`,
        );
      }
    },
    async saveUser() {
      if (this.isEditMode && this.hasUnreadableData) {
        this.warning(
          "Este cadastro tem dados que não puderam ser abertos agora. Salvar apagaria esses dados — tente de novo quando eles voltarem.",
          8000,
        );
        return;
      }
      const isValid = this.$refs.formStep?.validate();
      if (!isValid) {
        this.warning(
          "Por favor, preencha todos os campos obrigatórios corretamente.",
        );
        return;
      }
      this.loading = true;
      this.erro = "";
      try {
        await this.prepareUserData();
        let created = null;
        if (this.isEditMode) {
          await this.updateUser();
        } else {
          created = await this.createUser();
        }
        // Novo cadastro leva o id: a lista de origem destaca o card dele.
        this.$emit("updatePersonsPage", { personId: created?.id || null });
        this.closeAddNewUserDialog();
      } catch (err) {
        if (err instanceof Error && err.message.includes("responsável")) {
          this.warning(err.message);
          return;
        }
        this.handleSaveError(err);
      } finally {
        this.loading = false;
      }
    },
    async prepareUserData() {
      let myUserId = this.$store.getters["auth/userId"];
      if (!myUserId) {
        await this.$store.dispatch("auth/ensureAuth");
        myUserId = this.$store.getters["auth/userId"];
      }
      // Limpa campos que não devem ser enviados por padrão
      delete this.user.doctor_id;
      delete this.user.clinic_ids;
      delete this.user.guardian_id;
      delete this.user.password;
      if (this.targetUserType === "doctor") {
        this.user.clinic_ids = this.selectedClinics;
        delete this.user.referral_source;
        return;
      }
      if (
        this.targetUserType === "patient" &&
        this.isUnder18 &&
        this.isMinorEnabled
      ) {
        if (!this.guardianId) {
          throw new Error(
            "Selecione/busque um responsável válido antes de salvar.",
          );
        }
        this.user.guardian_id = this.guardianId;
      }
      if (this.userType === "doctor_solo") {
        this.user.doctor_id = myUserId;
        return;
      }
      if (this.userType === "doctor_clinic") {
        this.user.doctor_id = myUserId;
        this.user.clinic_ids = this.selectedClinics;
        return;
      }
      if (this.userType === "assistant") {
        this.user.clinic_ids = this.selectedClinics;
      }
      if (this.externalGuardianId) {
        this.user.guardian_id = this.externalGuardianId;
      }
    },
    async createUser() {
      const res = await http.post(this.apiConfig.createUrl, this.user);
      this.success(`${this.entityLabel()} cadastrado com sucesso!`);
      return res?.data?.data || null;
    },
    async updateUser() {
      await http.put(this.apiConfig.editUrl, this.user);
      this.success(`${this.entityLabel()} atualizado com sucesso!`);
      this.$emit("updatePersonsPageFromPersonCard");
    },
    handleSaveError(err) {
      console.error("Erro ao salvar:", err);
      if (err.response?.status === 422) {
        const errors = err.response.data?.errors;
        const message = errors ? Object.values(errors)[0]?.[0] : null;
        this.error(message || "Erro de validação.");
        return;
      }
      this.error(`Erro ao salvar ${this.entityLabel().toLowerCase()}.`);
    },
    resetGuardian() {
      this.guardianCpfInput = "";
      this.guardianLoading = false;
      this.guardianChecked = false;
      this.guardianExists = false;
      this.guardianName = "";
      this.guardianId = null;
      this.guardianError = "";
      this.guardianNotFound = false;
    },
    async searchGuardianByCpf() {
      this.guardianError = "";

      if (!(this.isUnder18 && this.isMinorEnabled)) {
        this.guardianError = "Ative 'Menor de idade' para buscar responsável.";
        return;
      }
      const cpf = this.onlyDigits(this.guardianCpfInput);
      if (!cpf || cpf.length !== 11) {
        this.guardianError = "Informe um CPF válido do responsável.";
        return;
      }
      this.guardianLoading = true;
      this.guardianChecked = false;
      this.guardianExists = false;
      this.guardianNotFound = false;
      this.guardianName = "";
      this.guardianId = null;
      try {
        const res = await http.get(`/api/patients/check-cpf/${cpf}`);
        this.guardianChecked = true;
        this.guardianExists = !!res.data.exists;
        if (!this.guardianExists) {
          // Não é erro: é o gancho para "Cadastrar responsável agora".
          this.guardianNotFound = true;
          return;
        }
        this.guardianId = res.data.patient_id || null;
        this.guardianName = res.data.name || "";
        if (!this.guardianId) {
          this.guardianError =
            "Responsável encontrado, mas sem ID retornado pela API.";
          this.guardianExists = false;
        }
      } catch (err) {
        console.error("Erro ao buscar responsável:", err);
        this.guardianError = "Erro ao buscar responsável.";
      } finally {
        this.guardianLoading = false;
      }
    },
    openGuardianRegister() {
      const cpf = this.onlyDigits(this.guardianCpfInput);
      if (cpf.length !== 11) {
        this.guardianError = "Informe um CPF válido do responsável.";
        return;
      }
      this.guardianRegisterOpen = true;
    },
    async onGuardianCreated() {
      // O diálogo aninhado já se fecha sozinho ao salvar; aqui só re-buscamos
      // o responsável recém-criado para preencher o guardian_id e vincular.
      this.guardianRegisterOpen = false;
      await this.searchGuardianByCpf();
      if (this.guardianExists && this.guardianId) {
        this.success("Responsável cadastrado e vinculado com sucesso!");
        // Atualiza a listagem de origem (o novo responsável já é um paciente).
        this.$emit("updatePersonsPage");
      } else {
        this.warning(
          "Responsável cadastrado, mas não foi possível vinculá-lo automaticamente. Busque pelo CPF.",
        );
      }
    },
    goBackToStep1() {
      this.currentStep = 1;
      this.erro = "";
      this.cpfChecked = false;
      this.cpfExists = false;
      this.existingPatientName = "";
      this.existingAlreadyLinked = false;
      this.existingUserRoleId = null; // ✅ Task 3
      this.selectedClinics = [];
      this.$nextTick(() => {
        this.$refs.cpfStep?.resetValidation?.();
      });
    },
    autoSelectSingleClinic() {
      if (
        !this.shouldShowClinicSelection &&
        !this.shouldShowClinicSelectionOnCpfStep
      )
        return;
      if (Array.isArray(this.clinics) && this.clinics.length === 1) {
        const onlyId = this.clinics[0]?.id;
        if (!onlyId) return;
        if (!this.selectedClinics?.length) {
          this.selectedClinics = [onlyId];
        }
      }
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
</style>
